/**
 * Case Studies Page - Filterable success stories - 21 languages
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, ArrowRight, TrendingUp, Clock, Users, Shield } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { RelatedPagesSection } from '@/components/sections/RelatedPagesSection';
import { generateBreadcrumbSchema, generateReviewSchema, generateFAQSchema, SITE_URL } from '@/lib/seo';
import { caseStudiesTier2 } from '@/lib/auxiliary-pages-tier2-i18n';

const content: Record<string, any> = {
  en: {
    title: 'Case Studies',
    subtitle: 'Real results from real partnerships. See how we help businesses scale.',
    badge: 'Success Stories',
    filterAll: 'All',
    filterIndustry: 'Industry',
    filterService: 'Service',
    viewCTA: 'View Case Study',
    contactCTA: 'Start Your Success Story',
    contactSubtitle: 'Ready to achieve similar results? Let\'s discuss your project.',
    industries: {
      ecommerce: 'E-commerce',
      energy: 'Energy / Solar',
      saas: 'SaaS',
      logistics: 'Logistics',
      telecom: 'Telecom',
      realestate: 'Real Estate',
    },
    services: {
      support: 'Customer Support',
      sales: 'Tele-sales',
      backoffice: 'Back-office',
      moderation: 'Content Moderation',
      web: 'Web Development',
      mobile: 'Mobile Apps',
      ai: 'AI Solutions',
    },
    cases: [
      {
        id: 'eu-ecommerce-csat-340',
        title: 'European E-Commerce Giant: 340% Customer Satisfaction Improvement',
        industry: 'ecommerce',
        service: 'support',
        client: 'Major EU Retailer',
        challenge: 'A leading European e-commerce retailer processing over 2 million orders monthly was struggling with an average response time of 18 hours across 12 languages. Their in-house team of 45 agents could not keep pace with seasonal demand surges, leading to a dismal CSAT score of 2.1 out of 5. Customer complaints on social media were damaging their brand reputation, and they were losing an estimated €4.7M annually in churned customers. The retailer needed a partner capable of delivering consistent, high-quality multilingual support across all European markets while maintaining their premium brand voice and reducing operational costs significantly.',
        solution: 'CALL IT DEV deployed 120 multilingual agents from its Casablanca hub, covering all 12 required languages with native or C2-level speakers. The team implemented AI-powered intelligent routing that matched customers to agents by language, issue type, and expertise level. A dedicated QA team conducted daily calibration sessions, and real-time sentiment analysis flagged escalation-worthy interactions within 30 seconds. Custom dashboards gave the client full visibility into every KPI. The onboarding process took just 21 days from contract signing to first live interaction, with comprehensive brand voice training ensuring consistent tone across all markets.',
        results: [
          { metric: '47min', label: 'Response Time (from 18hrs)' },
          { metric: '4.8/5', label: 'CSAT (from 2.1)' },
          { metric: '€2.3M', label: 'Annual Savings' },
          { metric: '99.7%', label: 'SLA Compliance' },
        ],
      },
      {
        id: 'fintech-unicorn-scaling',
        title: 'FinTech Unicorn: Seamless 24/7 Technical Support Scaling',
        industry: 'saas',
        service: 'support',
        client: 'Series C FinTech Startup',
        challenge: 'A Series C fintech unicorn valued at $2.1B needed to scale its customer support operation from 50 to 500 agents within 90 days to support a major product launch across 8 new markets. Their existing team lacked the technical depth for complex payment processing queries, and regulatory requirements demanded SOC 2 compliance from day one. The company had already approached three other BPO providers who estimated 6-month timelines. Internal recruitment was not viable given the specialized skills required — agents needed fluency in financial terminology, anti-fraud detection protocols, and real-time transaction troubleshooting across multiple payment rails.',
        solution: 'CALL IT DEV leveraged its Madrid hub to build a specialized fintech support division. The team developed a custom CRM integration with the client\'s proprietary payment platform, enabling agents to view real-time transaction data and resolve issues without escalation. A real-time fraud detection workflow was implemented where agents could flag suspicious patterns that fed directly into the client\'s ML fraud engine. Three recruitment waves were executed simultaneously across Casablanca, Madrid, and Dubai, with a proprietary assessment battery filtering for financial literacy and technical aptitude. SOC 2 Type II compliance was achieved within the first 30 days through pre-existing infrastructure.',
        results: [
          { metric: '78 days', label: '500 Agents Deployed' },
          { metric: '94%', label: 'First-Call Resolution' },
          { metric: '-62%', label: 'Customer Churn Reduction' },
          { metric: 'Day 1', label: 'SOC 2 Compliant' },
        ],
      },
      {
        id: 'healthcare-saas-hipaa',
        title: 'Healthcare SaaS: HIPAA-Compliant Multilingual Support',
        industry: 'saas',
        service: 'support',
        client: 'US Telehealth Platform',
        challenge: 'A fast-growing US telehealth platform serving 3.2 million patients needed HIPAA-compliant customer support across Arabic, French, and English to serve their expanding Middle Eastern and North African patient base. Previous attempts with US-based support centers resulted in costs exceeding $85 per interaction and average wait times of 12 minutes. The platform required agents who understood both medical terminology and cultural nuances of MENA healthcare interactions. All communications needed end-to-end encryption, and every agent required individual HIPAA certification with ongoing compliance auditing.',
        solution: 'CALL IT DEV established a dedicated secure facility in its Dubai office with physically segregated workspaces, encrypted communications infrastructure, and biometric access controls. Every agent completed HIPAA certification through the platform\'s approved training program, with quarterly re-certification audits. The team deployed specialized medical terminology training in Arabic, French, and English, with cultural sensitivity modules addressing healthcare communication norms across MENA markets. Real-time translation memory tools helped agents maintain consistent medical terminology. An automated compliance monitoring system tracked every interaction for HIPAA adherence, with zero-tolerance escalation protocols for potential violations.',
        results: [
          { metric: '0', label: 'Compliance Violations (18mo)' },
          { metric: '71', label: 'NPS (from 32)' },
          { metric: '-40%', label: 'Cost Reduction vs US' },
          { metric: '<3min', label: 'Avg Wait Time' },
        ],
      },
      {
        id: 'ecommerce-cx',
        title: 'E-commerce CX Transformation',
        industry: 'ecommerce',
        service: 'support',
        client: 'Anonymous (NDA)',
        challenge: 'A fast-growing e-commerce brand struggled with 4-hour response times and declining CSAT during peak seasons.',
        solution: 'Deployed a 24/7 multilingual support team (EN/FR/ES) with dedicated QA and real-time escalation protocols.',
        results: [
          { metric: '-65%', label: 'First Response Time' },
          { metric: '+18 pts', label: 'CSAT Improvement' },
          { metric: '0', label: 'Missed SLAs' },
        ],
      },
      {
        id: 'solar-sales',
        title: 'Solar Energy Sales Pipeline',
        industry: 'energy',
        service: 'sales',
        client: 'Anonymous (NDA)',
        challenge: 'Solar installer needed to scale outbound sales without increasing CAC while maintaining lead quality.',
        solution: 'Built a dedicated SDR team with custom scripts, CRM integration, and weekly calibration sessions.',
        results: [
          { metric: '+22%', label: 'Conversion Rate' },
          { metric: '-30%', label: 'Cost per Lead' },
          { metric: '3x', label: 'Pipeline Volume' },
        ],
      },
      {
        id: 'saas-onboarding',
        title: 'SaaS Onboarding Acceleration',
        industry: 'saas',
        service: 'support',
        client: 'Anonymous (NDA)',
        challenge: 'B2B SaaS company faced high early-stage churn due to poor onboarding experience and slow support.',
        solution: 'Implemented proactive onboarding support with L2 technical agents and automated health scoring.',
        results: [
          { metric: '-40%', label: 'Early Churn' },
          { metric: '+35%', label: 'Feature Adoption' },
          { metric: '<2h', label: 'Avg Resolution' },
        ],
      },
      {
        id: 'logistics-backoffice',
        title: 'Logistics Back-office Scaling',
        industry: 'logistics',
        service: 'backoffice',
        client: 'Anonymous (NDA)',
        challenge: 'Last-mile delivery company needed to process 10x order volume without proportional headcount increase.',
        solution: 'Deployed trained back-office team with automated workflows and exception handling protocols.',
        results: [
          { metric: '99.2%', label: 'Processing Accuracy' },
          { metric: '10x', label: 'Volume Handled' },
          { metric: '-45%', label: 'Cost per Order' },
        ],
      },
      {
        id: 'telecom-retention',
        title: 'Telecom Retention Campaign',
        industry: 'telecom',
        service: 'sales',
        client: 'Anonymous (NDA)',
        challenge: 'Mobile operator faced 25% annual churn and needed a proactive retention strategy.',
        solution: 'Launched predictive churn prevention with specialized win-back agents and personalized offers.',
        results: [
          { metric: '-35%', label: 'Churn Rate' },
          { metric: '+28%', label: 'Save Rate' },
          { metric: '4.5/5', label: 'CSAT Score' },
        ],
      },
      {
        id: 'realestate-leads',
        title: 'Real Estate Lead Qualification',
        industry: 'realestate',
        service: 'sales',
        client: 'Anonymous (NDA)',
        challenge: 'Real estate agency wasted agent time on unqualified leads from multiple marketing channels.',
        solution: 'Built inbound/outbound qualification team with scoring criteria and CRM automation.',
        results: [
          { metric: '+55%', label: 'Qualified Leads' },
          { metric: '-60%', label: 'Agent Time Wasted' },
          { metric: '+20%', label: 'Conversion Rate' },
        ],
      },
      {
        id: 'fintech-moderation',
        title: 'Fintech Content Moderation',
        industry: 'saas',
        service: 'moderation',
        client: 'Anonymous (NDA)',
        challenge: 'Payment platform needed to review user-generated content for fraud and policy violations at scale.',
        solution: 'Deployed AI-assisted moderation team with 24/7 coverage and real-time escalation protocols.',
        results: [
          { metric: '99.8%', label: 'Detection Rate' },
          { metric: '<30min', label: 'Review TAT' },
          { metric: '-75%', label: 'Fraud Incidents' },
        ],
      },
      {
        id: 'ecom-platform-build',
        title: 'E-commerce Platform Rebuild',
        industry: 'ecommerce',
        service: 'web',
        client: 'Anonymous (NDA)',
        challenge: 'Legacy e-commerce platform had 8s load times and poor mobile experience affecting sales.',
        solution: 'Built headless commerce solution with React, achieving sub-2s load times and 95+ Lighthouse scores.',
        results: [
          { metric: '+45%', label: 'Mobile Conversions' },
          { metric: '1.2s', label: 'Load Time' },
          { metric: '98', label: 'Lighthouse Score' },
        ],
      },
      {
        id: 'saas-mobile-app',
        title: 'SaaS Mobile App Launch',
        industry: 'saas',
        service: 'mobile',
        client: 'Anonymous (NDA)',
        challenge: 'B2B SaaS needed mobile app for field sales team with offline sync and CRM integration.',
        solution: 'Delivered React Native app with offline-first architecture and real-time Salesforce sync.',
        results: [
          { metric: '4.7/5', label: 'App Store Rating' },
          { metric: '+30%', label: 'Field Productivity' },
          { metric: '12 weeks', label: 'Time to Market' },
        ],
      },
      {
        id: 'logistics-ai',
        title: 'Logistics AI Optimization',
        industry: 'logistics',
        service: 'ai',
        client: 'Anonymous (NDA)',
        challenge: 'Delivery company struggled with route optimization and demand forecasting accuracy.',
        solution: 'Implemented ML-based demand forecasting and routing optimization with real-time adjustments.',
        results: [
          { metric: '-22%', label: 'Delivery Costs' },
          { metric: '+15%', label: 'On-Time Rate' },
          { metric: '92%', label: 'Forecast Accuracy' },
        ],
      },
    ],
  },
  fr: {
    title: 'Études de Cas',
    subtitle: 'Résultats réels de partenariats réels. Découvrez comment nous aidons les entreprises à scaler.',
    badge: 'Success Stories',
    filterAll: 'Tous',
    filterIndustry: 'Industrie',
    filterService: 'Service',
    viewCTA: 'Voir l\'étude de cas',
    contactCTA: 'Démarrez Votre Success Story',
    contactSubtitle: 'Prêt à atteindre des résultats similaires ? Discutons de votre projet.',
    industries: {
      ecommerce: 'E-commerce',
      energy: 'Énergie / Solaire',
      saas: 'SaaS',
      logistics: 'Logistique',
      telecom: 'Télécom',
      realestate: 'Immobilier',
    },
    services: {
      support: 'Support Client',
      sales: 'Télévente',
      backoffice: 'Back-office',
      moderation: 'Modération de Contenu',
      web: 'Développement Web',
      mobile: 'Applications Mobiles',
      ai: 'Solutions IA',
    },
    cases: [
      {
        id: 'ecommerce-cx',
        title: 'Transformation CX E-commerce',
        industry: 'ecommerce',
        service: 'support',
        client: 'Anonyme (NDA)',
        challenge: 'Une marque e-commerce en forte croissance luttait avec des temps de réponse de 4 heures et un CSAT en baisse.',
        solution: 'Déploiement d\'une équipe support multilingue 24/7 (EN/FR/ES) avec QA dédié et protocoles d\'escalade.',
        results: [
          { metric: '-65%', label: 'Temps de première réponse' },
          { metric: '+18 pts', label: 'Amélioration CSAT' },
          { metric: '0', label: 'SLAs manqués' },
        ],
      },
      {
        id: 'solar-sales',
        title: 'Pipeline Ventes Énergie Solaire',
        industry: 'energy',
        service: 'sales',
        client: 'Anonyme (NDA)',
        challenge: 'Un installateur solaire devait scaler les ventes sortantes sans augmenter le CAC.',
        solution: 'Création d\'une équipe SDR dédiée avec scripts personnalisés et intégration CRM.',
        results: [
          { metric: '+22%', label: 'Taux de conversion' },
          { metric: '-30%', label: 'Coût par lead' },
          { metric: '3x', label: 'Volume pipeline' },
        ],
      },
      {
        id: 'saas-onboarding',
        title: 'Accélération Onboarding SaaS',
        industry: 'saas',
        service: 'support',
        client: 'Anonyme (NDA)',
        challenge: 'Une entreprise SaaS B2B faisait face à un churn élevé dû à un mauvais onboarding.',
        solution: 'Support onboarding proactif avec agents techniques L2 et scoring de santé automatisé.',
        results: [
          { metric: '-40%', label: 'Churn précoce' },
          { metric: '+35%', label: 'Adoption features' },
          { metric: '<2h', label: 'Résolution moyenne' },
        ],
      },
      {
        id: 'logistics-backoffice',
        title: 'Scaling Back-office Logistique',
        industry: 'logistics',
        service: 'backoffice',
        client: 'Anonyme (NDA)',
        challenge: 'Une entreprise de livraison dernier kilomètre devait traiter 10x le volume sans augmenter les effectifs.',
        solution: 'Équipe back-office formée avec workflows automatisés et gestion des exceptions.',
        results: [
          { metric: '99.2%', label: 'Précision traitement' },
          { metric: '10x', label: 'Volume traité' },
          { metric: '-45%', label: 'Coût par commande' },
        ],
      },
      {
        id: 'telecom-retention',
        title: 'Campagne Rétention Télécom',
        industry: 'telecom',
        service: 'sales',
        client: 'Anonyme (NDA)',
        challenge: 'Un opérateur mobile faisait face à 25% de churn annuel.',
        solution: 'Prévention churn prédictive avec agents win-back spécialisés et offres personnalisées.',
        results: [
          { metric: '-35%', label: 'Taux de churn' },
          { metric: '+28%', label: 'Taux de save' },
          { metric: '4.5/5', label: 'Score CSAT' },
        ],
      },
      {
        id: 'realestate-leads',
        title: 'Qualification Leads Immobilier',
        industry: 'realestate',
        service: 'sales',
        client: 'Anonyme (NDA)',
        challenge: 'Une agence immobilière perdait du temps sur des leads non qualifiés.',
        solution: 'Équipe de qualification inbound/outbound avec critères de scoring et automatisation CRM.',
        results: [
          { metric: '+55%', label: 'Leads qualifiés' },
          { metric: '-60%', label: 'Temps agent perdu' },
          { metric: '+20%', label: 'Taux de conversion' },
        ],
      },
      {
        id: 'fintech-moderation',
        title: 'Modération Contenu Fintech',
        industry: 'saas',
        service: 'moderation',
        client: 'Anonyme (NDA)',
        challenge: 'Plateforme de paiement devait modérer le contenu utilisateur pour détecter fraudes et violations.',
        solution: 'Équipe de modération assistée par IA avec couverture 24/7 et protocoles d\'escalade.',
        results: [
          { metric: '99.8%', label: 'Taux de détection' },
          { metric: '<30min', label: 'TAT Review' },
          { metric: '-75%', label: 'Incidents fraude' },
        ],
      },
      {
        id: 'ecom-platform-build',
        title: 'Refonte Plateforme E-commerce',
        industry: 'ecommerce',
        service: 'web',
        client: 'Anonyme (NDA)',
        challenge: 'Plateforme legacy avec temps de chargement 8s et mauvaise expérience mobile.',
        solution: 'Solution headless commerce en React avec temps de chargement <2s et score Lighthouse 95+.',
        results: [
          { metric: '+45%', label: 'Conversions mobile' },
          { metric: '1.2s', label: 'Temps chargement' },
          { metric: '98', label: 'Score Lighthouse' },
        ],
      },
      {
        id: 'saas-mobile-app',
        title: 'Lancement App Mobile SaaS',
        industry: 'saas',
        service: 'mobile',
        client: 'Anonyme (NDA)',
        challenge: 'SaaS B2B avait besoin d\'une app mobile pour équipes terrain avec sync offline et CRM.',
        solution: 'App React Native offline-first avec synchronisation Salesforce en temps réel.',
        results: [
          { metric: '4.7/5', label: 'Note App Store' },
          { metric: '+30%', label: 'Productivité terrain' },
          { metric: '12 sem', label: 'Time to Market' },
        ],
      },
      {
        id: 'logistics-ai',
        title: 'Optimisation IA Logistique',
        industry: 'logistics',
        service: 'ai',
        client: 'Anonyme (NDA)',
        challenge: 'Entreprise de livraison avec problèmes d\'optimisation routes et prévisions demande.',
        solution: 'ML pour prévision demande et optimisation routes avec ajustements temps réel.',
        results: [
          { metric: '-22%', label: 'Coûts livraison' },
          { metric: '+15%', label: 'Taux ponctualité' },
          { metric: '92%', label: 'Précision prévisions' },
        ],
      },
    ],
  },
  es: {
    title: 'Casos de Éxito',
    subtitle: 'Resultados reales de partnerships reales. Descubre cómo ayudamos a las empresas a escalar.',
    badge: 'Historias de Éxito',
    filterAll: 'Todos',
    filterIndustry: 'Industria',
    filterService: 'Servicio',
    viewCTA: 'Ver caso de estudio',
    contactCTA: 'Empieza Tu Historia de Éxito',
    contactSubtitle: '¿Listo para lograr resultados similares? Hablemos de tu proyecto.',
    industries: {
      ecommerce: 'E-commerce',
      energy: 'Energía / Solar',
      saas: 'SaaS',
      logistics: 'Logística',
      telecom: 'Telecom',
      realestate: 'Inmobiliaria',
    },
    services: {
      support: 'Soporte al Cliente',
      sales: 'Televentas',
      backoffice: 'Back-office',
      moderation: 'Moderación de Contenido',
      web: 'Desarrollo Web',
      mobile: 'Apps Móviles',
      ai: 'Soluciones IA',
    },
    cases: [
      {
        id: 'ecommerce-cx',
        title: 'Transformación CX E-commerce',
        industry: 'ecommerce',
        service: 'support',
        client: 'Anónimo (NDA)',
        challenge: 'Una marca e-commerce en rápido crecimiento luchaba con tiempos de respuesta de 4 horas.',
        solution: 'Desplegamos equipo de soporte multilingüe 24/7 (EN/FR/ES) con QA dedicado.',
        results: [
          { metric: '-65%', label: 'Tiempo primera respuesta' },
          { metric: '+18 pts', label: 'Mejora CSAT' },
          { metric: '0', label: 'SLAs incumplidos' },
        ],
      },
      {
        id: 'solar-sales',
        title: 'Pipeline Ventas Energía Solar',
        industry: 'energy',
        service: 'sales',
        client: 'Anónimo (NDA)',
        challenge: 'Instalador solar necesitaba escalar ventas sin aumentar CAC.',
        solution: 'Equipo SDR dedicado con scripts personalizados e integración CRM.',
        results: [
          { metric: '+22%', label: 'Tasa de conversión' },
          { metric: '-30%', label: 'Coste por lead' },
          { metric: '3x', label: 'Volumen pipeline' },
        ],
      },
      {
        id: 'saas-onboarding',
        title: 'Aceleración Onboarding SaaS',
        industry: 'saas',
        service: 'support',
        client: 'Anónimo (NDA)',
        challenge: 'Empresa SaaS B2B enfrentaba alto churn por mal onboarding.',
        solution: 'Soporte onboarding proactivo con agentes técnicos L2.',
        results: [
          { metric: '-40%', label: 'Churn temprano' },
          { metric: '+35%', label: 'Adopción features' },
          { metric: '<2h', label: 'Resolución promedio' },
        ],
      },
      {
        id: 'logistics-backoffice',
        title: 'Escalado Back-office Logística',
        industry: 'logistics',
        service: 'backoffice',
        client: 'Anónimo (NDA)',
        challenge: 'Empresa de última milla necesitaba procesar 10x volumen.',
        solution: 'Equipo back-office con workflows automatizados.',
        results: [
          { metric: '99.2%', label: 'Precisión procesamiento' },
          { metric: '10x', label: 'Volumen gestionado' },
          { metric: '-45%', label: 'Coste por pedido' },
        ],
      },
      {
        id: 'telecom-retention',
        title: 'Campaña Retención Telecom',
        industry: 'telecom',
        service: 'sales',
        client: 'Anónimo (NDA)',
        challenge: 'Operador móvil con 25% churn anual.',
        solution: 'Prevención churn predictiva con agentes especializados.',
        results: [
          { metric: '-35%', label: 'Tasa de churn' },
          { metric: '+28%', label: 'Tasa de save' },
          { metric: '4.5/5', label: 'Score CSAT' },
        ],
      },
      {
        id: 'realestate-leads',
        title: 'Cualificación Leads Inmobiliaria',
        industry: 'realestate',
        service: 'sales',
        client: 'Anónimo (NDA)',
        challenge: 'Agencia inmobiliaria perdía tiempo en leads no cualificados.',
        solution: 'Equipo cualificación con criterios scoring y automatización CRM.',
        results: [
          { metric: '+55%', label: 'Leads cualificados' },
          { metric: '-60%', label: 'Tiempo agente perdido' },
          { metric: '+20%', label: 'Tasa conversión' },
        ],
      },
      {
        id: 'fintech-moderation',
        title: 'Moderación Contenido Fintech',
        industry: 'saas',
        service: 'moderation',
        client: 'Anónimo (NDA)',
        challenge: 'Plataforma de pagos necesitaba moderar contenido por fraude y violaciones.',
        solution: 'Equipo de moderación asistido por IA con cobertura 24/7.',
        results: [
          { metric: '99.8%', label: 'Tasa detección' },
          { metric: '<30min', label: 'TAT Review' },
          { metric: '-75%', label: 'Incidentes fraude' },
        ],
      },
      {
        id: 'ecom-platform-build',
        title: 'Reconstrucción Plataforma E-commerce',
        industry: 'ecommerce',
        service: 'web',
        client: 'Anónimo (NDA)',
        challenge: 'Plataforma legacy con tiempos de carga de 8s y mala experiencia móvil.',
        solution: 'Solución headless commerce en React con tiempos <2s y Lighthouse 95+.',
        results: [
          { metric: '+45%', label: 'Conversiones móvil' },
          { metric: '1.2s', label: 'Tiempo carga' },
          { metric: '98', label: 'Score Lighthouse' },
        ],
      },
      {
        id: 'saas-mobile-app',
        title: 'Lanzamiento App Móvil SaaS',
        industry: 'saas',
        service: 'mobile',
        client: 'Anónimo (NDA)',
        challenge: 'SaaS B2B necesitaba app móvil para equipo de campo con sync offline.',
        solution: 'App React Native offline-first con sincronización Salesforce.',
        results: [
          { metric: '4.7/5', label: 'Rating App Store' },
          { metric: '+30%', label: 'Productividad campo' },
          { metric: '12 sem', label: 'Time to Market' },
        ],
      },
      {
        id: 'logistics-ai',
        title: 'Optimización IA Logística',
        industry: 'logistics',
        service: 'ai',
        client: 'Anónimo (NDA)',
        challenge: 'Empresa de delivery con problemas de optimización de rutas.',
        solution: 'ML para pronóstico de demanda y optimización de rutas en tiempo real.',
        results: [
          { metric: '-22%', label: 'Costos delivery' },
          { metric: '+15%', label: 'Tasa puntualidad' },
          { metric: '92%', label: 'Precisión pronóstico' },
        ],
      },
    ],
  },
  ar: {
    title: 'دراسات الحالة',
    subtitle: 'نتائج حقيقية من شراكات حقيقية. اكتشف كيف نساعد الشركات على التوسع.',
    badge: 'قصص النجاح',
    filterAll: 'الكل',
    filterIndustry: 'القطاع',
    filterService: 'الخدمة',
    viewCTA: 'عرض دراسة الحالة',
    contactCTA: 'ابدأ قصة نجاحك',
    contactSubtitle: 'مستعد لتحقيق نتائج مماثلة؟ دعنا نناقش مشروعك.',
    industries: {
      ecommerce: 'التجارة الإلكترونية',
      energy: 'الطاقة / الشمسية',
      saas: 'SaaS',
      logistics: 'اللوجستيات',
      telecom: 'الاتصالات',
      realestate: 'العقارات',
    },
    services: {
      support: 'دعم العملاء',
      sales: 'المبيعات الهاتفية',
      backoffice: 'المكتب الخلفي',
      moderation: 'إشراف المحتوى',
      web: 'تطوير الويب',
      mobile: 'تطبيقات الموبايل',
      ai: 'حلول الذكاء الاصطناعي',
    },
    cases: [
      {
        id: 'ecommerce-cx',
        title: 'تحول تجربة العملاء في التجارة الإلكترونية',
        industry: 'ecommerce',
        service: 'support',
        client: 'مجهول (NDA)',
        challenge: 'علامة تجارية إلكترونية سريعة النمو عانت من أوقات استجابة 4 ساعات.',
        solution: 'نشر فريق دعم متعدد اللغات 24/7 مع ضمان الجودة المخصص.',
        results: [
          { metric: '-65%', label: 'وقت الاستجابة الأولى' },
          { metric: '+18 نقطة', label: 'تحسين CSAT' },
          { metric: '0', label: 'SLAs مفقودة' },
        ],
      },
      {
        id: 'solar-sales',
        title: 'خط أنابيب مبيعات الطاقة الشمسية',
        industry: 'energy',
        service: 'sales',
        client: 'مجهول (NDA)',
        challenge: 'مُركب طاقة شمسية احتاج لتوسيع المبيعات دون زيادة CAC.',
        solution: 'بناء فريق SDR مخصص مع نصوص مخصصة وتكامل CRM.',
        results: [
          { metric: '+22%', label: 'معدل التحويل' },
          { metric: '-30%', label: 'تكلفة لكل عميل محتمل' },
          { metric: '3x', label: 'حجم خط الأنابيب' },
        ],
      },
      {
        id: 'saas-onboarding',
        title: 'تسريع تأهيل SaaS',
        industry: 'saas',
        service: 'support',
        client: 'مجهول (NDA)',
        challenge: 'شركة SaaS B2B واجهت churn عالي بسبب تجربة تأهيل سيئة.',
        solution: 'دعم تأهيل استباقي مع وكلاء تقنيين L2.',
        results: [
          { metric: '-40%', label: 'Churn مبكر' },
          { metric: '+35%', label: 'اعتماد الميزات' },
          { metric: '<2 ساعة', label: 'متوسط الحل' },
        ],
      },
      {
        id: 'logistics-backoffice',
        title: 'توسيع المكتب الخلفي اللوجستي',
        industry: 'logistics',
        service: 'backoffice',
        client: 'مجهول (NDA)',
        challenge: 'شركة توصيل الميل الأخير احتاجت لمعالجة 10x الحجم.',
        solution: 'فريق مكتب خلفي مدرب مع سير عمل آلي.',
        results: [
          { metric: '99.2%', label: 'دقة المعالجة' },
          { metric: '10x', label: 'الحجم المعالج' },
          { metric: '-45%', label: 'التكلفة لكل طلب' },
        ],
      },
      {
        id: 'telecom-retention',
        title: 'حملة الاحتفاظ بالاتصالات',
        industry: 'telecom',
        service: 'sales',
        client: 'مجهول (NDA)',
        challenge: 'مشغل جوال واجه 25% churn سنوي.',
        solution: 'منع churn تنبؤي مع وكلاء متخصصين.',
        results: [
          { metric: '-35%', label: 'معدل Churn' },
          { metric: '+28%', label: 'معدل الحفظ' },
          { metric: '4.5/5', label: 'درجة CSAT' },
        ],
      },
      {
        id: 'realestate-leads',
        title: 'تأهيل العملاء المحتملين العقاريين',
        industry: 'realestate',
        service: 'sales',
        client: 'مجهول (NDA)',
        challenge: 'وكالة عقارية أضاعت الوقت على عملاء محتملين غير مؤهلين.',
        solution: 'فريق تأهيل مع معايير تسجيل وأتمتة CRM.',
        results: [
          { metric: '+55%', label: 'عملاء محتملين مؤهلين' },
          { metric: '-60%', label: 'وقت الوكيل المهدر' },
          { metric: '+20%', label: 'معدل التحويل' },
        ],
      },
      {
        id: 'fintech-moderation',
        title: 'إشراف محتوى التكنولوجيا المالية',
        industry: 'saas',
        service: 'moderation',
        client: 'مجهول (NDA)',
        challenge: 'منصة دفع احتاجت لمراجعة المحتوى للكشف عن الاحتيال.',
        solution: 'فريق إشراف مدعوم بالذكاء الاصطناعي مع تغطية 24/7.',
        results: [
          { metric: '99.8%', label: 'معدل الكشف' },
          { metric: '<30 دقيقة', label: 'TAT المراجعة' },
          { metric: '-75%', label: 'حوادث الاحتيال' },
        ],
      },
      {
        id: 'ecom-platform-build',
        title: 'إعادة بناء منصة التجارة الإلكترونية',
        industry: 'ecommerce',
        service: 'web',
        client: 'مجهول (NDA)',
        challenge: 'منصة قديمة بأوقات تحميل 8 ثوانٍ وتجربة جوال سيئة.',
        solution: 'حل التجارة الإلكترونية بدون رأس مع React وأوقات تحميل أقل من 2 ثانية.',
        results: [
          { metric: '+45%', label: 'تحويلات الجوال' },
          { metric: '1.2 ثانية', label: 'وقت التحميل' },
          { metric: '98', label: 'درجة Lighthouse' },
        ],
      },
      {
        id: 'saas-mobile-app',
        title: 'إطلاق تطبيق SaaS للجوال',
        industry: 'saas',
        service: 'mobile',
        client: 'مجهول (NDA)',
        challenge: 'SaaS B2B احتاج تطبيق جوال لفريق المبيعات الميدانية مع مزامنة غير متصلة.',
        solution: 'تطبيق React Native مع بنية أولاً غير متصلة ومزامنة Salesforce.',
        results: [
          { metric: '4.7/5', label: 'تقييم App Store' },
          { metric: '+30%', label: 'إنتاجية الميدان' },
          { metric: '12 أسبوع', label: 'وقت الوصول للسوق' },
        ],
      },
      {
        id: 'logistics-ai',
        title: 'تحسين الذكاء الاصطناعي للوجستيات',
        industry: 'logistics',
        service: 'ai',
        client: 'مجهول (NDA)',
        challenge: 'شركة توصيل واجهت مشاكل في تحسين المسارات والتنبؤ بالطلب.',
        solution: 'تنبؤ بالطلب قائم على ML وتحسين المسارات في الوقت الفعلي.',
        results: [
          { metric: '-22%', label: 'تكاليف التوصيل' },
          { metric: '+15%', label: 'معدل الالتزام بالمواعيد' },
          { metric: '92%', label: 'دقة التنبؤ' },
        ],
      },
    ],
  },
  ...Object.fromEntries(
    Object.entries(caseStudiesTier2).map(([lang, t2]) => [lang, {
      ...t2,
      cases: [], // filled by safeCases fallback to EN below
    }])
  ),
};

const caseStudiesSeoTitle: Record<string, string> = {
  en: 'Case Studies | Call IT Dev',
  fr: 'Études de Cas | Call IT Dev',
  es: 'Casos de Éxito | Call IT Dev',
  ar: 'دراسات الحالة | Call IT Dev',
  de: 'Fallstudien | Call IT Dev',
  it: 'Casi di Studio | Call IT Dev',
  pt: 'Estudos de Caso | Call IT Dev',
  nl: 'Case Studies | Call IT Dev',
  pl: 'Studia Przypadków | Call IT Dev',
  ru: 'Кейсы | Call IT Dev',
  zh: '案例研究 | Call IT Dev',
  ja: '導入事例 | Call IT Dev',
  tr: 'Vaka Çalışmaları | Call IT Dev',
  ko: '사례 연구 | Call IT Dev',
  sv: 'Case Studies | Call IT Dev',
  da: 'Case Studies | Call IT Dev',
  hi: 'केस स्टडीज़ | Call IT Dev',
  ro: 'Studii de Caz | Call IT Dev',
  cs: 'Případové studie | Call IT Dev',
  hu: 'Esettanulmányok | Call IT Dev',
  fi: 'Tapaustutkimukset | Call IT Dev',
};

const caseStudiesSectionLabels: Record<string, { challenge: string; solution: string }> = {
  en: { challenge: 'Challenge', solution: 'Solution' },
  fr: { challenge: 'Défi', solution: 'Solution' },
  es: { challenge: 'Desafío', solution: 'Solución' },
  ar: { challenge: 'التحدي', solution: 'الحل' },
};

export default function CaseStudiesPage() {
  const { language } = useLanguage();
  const [industryFilter, setIndustryFilter] = useState<string>('all');
  const [serviceFilter, setServiceFilter] = useState<string>('all');
  
  const lang = (language in content) ? language as keyof typeof content : 'en';
  const c = content[lang] || content.en;
  const uiLabels = caseStudiesSectionLabels[language] || caseStudiesSectionLabels.en;
  const safeCases = Array.isArray(c.cases) && c.cases.length > 0
    ? c.cases
    : (content.en as any).cases.slice(0, 4);

  const filteredCases = safeCases.filter(cs => {
    if (industryFilter !== 'all' && cs.industry !== industryFilter) return false;
    if (serviceFilter !== 'all' && cs.service !== serviceFilter) return false;
    return true;
  });

  return (
    <MainLayout>
      <SEOHead 
        title={caseStudiesSeoTitle[language] || caseStudiesSeoTitle.en}
        description={c.subtitle}
        jsonLd={[
          generateBreadcrumbSchema([
            { name: 'Home', href: `/${language}` },
            { name: 'Resources', href: `/${language}/resources` },
            { name: 'Case Studies', href: `/${language}/case-studies` },
          ]),
          generateReviewSchema([
            { author: 'European E-commerce Director', reviewBody: 'Call IT Dev reduced our support costs by 55% while improving CSAT from 78% to 94%.', ratingValue: 5 },
            { author: 'SaaS VP of Customer Success', reviewBody: 'Moving to Call IT Dev was the best decision. 24/7 coverage, native speakers in 8 languages, NPS jumped 30 points.', ratingValue: 5 },
            { author: 'FinTech Operations Lead', reviewBody: 'Their compliance knowledge is exceptional. GDPR, PCI DSS — response times dropped below 15 seconds.', ratingValue: 5 },
          ]),
          // Article JSON-LD for each featured case study
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'European E-Commerce Giant: 340% Customer Satisfaction Improvement',
            description: 'How CALL IT DEV deployed 120 multilingual agents to reduce response times from 18 hours to 47 minutes and improve CSAT from 2.1 to 4.8/5.',
            author: { '@type': 'Organization', name: 'CALL IT DEV' },
            publisher: { '@type': 'Organization', name: 'CALL IT DEV', url: SITE_URL },
            datePublished: '2025-09-15',
            dateModified: '2026-02-20',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'FinTech Unicorn: Seamless 24/7 Technical Support Scaling',
            description: 'Scaling from 50 to 500 SOC 2-compliant agents in 78 days with 94% first-call resolution for a Series C fintech.',
            author: { '@type': 'Organization', name: 'CALL IT DEV' },
            publisher: { '@type': 'Organization', name: 'CALL IT DEV', url: SITE_URL },
            datePublished: '2025-11-01',
            dateModified: '2026-01-15',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Healthcare SaaS: HIPAA-Compliant Multilingual Support',
            description: 'Zero HIPAA violations in 18 months while delivering Arabic/French/English telehealth support at 40% lower cost.',
            author: { '@type': 'Organization', name: 'CALL IT DEV' },
            publisher: { '@type': 'Organization', name: 'CALL IT DEV', url: SITE_URL },
            datePublished: '2025-12-10',
            dateModified: '2026-03-01',
          },
          generateFAQSchema([
            { question: 'How does CALL IT DEV ensure quality in outsourced customer support?', answer: 'CALL IT DEV employs a multi-layered quality assurance framework including daily calibration sessions, real-time sentiment analysis, automated QA scoring on 100% of interactions, and dedicated QA managers for each account. Our average CSAT across all clients is 4.8/5.' },
            { question: 'How quickly can CALL IT DEV scale a support team?', answer: 'We can deploy up to 500 trained agents within 90 days thanks to our three-hub model (Casablanca, Madrid, Dubai), proprietary recruitment assessment tools, and pre-trained talent pools. Our fastest deployment was 78 days for a 500-agent fintech operation.' },
            { question: 'What industries does CALL IT DEV serve with outsourcing?', answer: 'We serve e-commerce, fintech, healthcare/telehealth, SaaS, telecommunications, logistics, real estate, energy, and more. Each vertical has a dedicated practice lead with deep domain expertise.' },
            { question: 'Is CALL IT DEV compliant with HIPAA and SOC 2?', answer: 'Yes. Our facilities maintain SOC 2 Type II certification, HIPAA compliance with individually certified agents, ISO 27001, PCI DSS, and full GDPR compliance. Our Dubai facility features physically segregated secure workspaces for healthcare clients.' },
            { question: 'What languages does CALL IT DEV support?', answer: 'We provide native or C2-level support in 28 languages including English, French, Spanish, Arabic, German, Italian, Portuguese, Dutch, Polish, Russian, Chinese, Japanese, Korean, Turkish, Swedish, Danish, Hindi, and more.' },
            { question: 'What cost savings can I expect from outsourcing to CALL IT DEV?', answer: 'Clients typically achieve 40-70% cost reduction compared to onshore operations. Our European e-commerce case study demonstrated €2.3M annual savings, while our healthcare SaaS client achieved 40% cost reduction versus US-based support centers.' },
          ]),
        ]}
      />
      
      {/* Hero */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-light to-navy" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(6,182,212,0.15),transparent_50%)]" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-6 bg-cyan/20 text-cyan border-cyan/30">{c.badge}</Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              {c.title}
            </h1>
            <p className="text-xl text-white/70">{c.subtitle}</p>
          </div>
        </div>
      </section>
      
      {/* Filters */}
      <section className="py-8 bg-muted/50 border-b border-border/50 sticky top-16 z-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Filter className="h-4 w-4" />
              <span className="text-sm font-medium">{c.filterIndustry}:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={industryFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setIndustryFilter('all')}
              >
                {c.filterAll}
              </Button>
              {Object.entries(c.industries).map(([key, label]) => (
                <Button
                  key={key}
                  size="sm"
                  variant={industryFilter === key ? 'default' : 'outline'}
                  onClick={() => setIndustryFilter(key)}
                >
                  {label as string}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      {/* Cases Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCases.length > 0 ? (
              filteredCases.map((cs, i) => (
                <motion.div
                  key={cs.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card className="h-full border-border/50 hover:border-cyan/50 transition-colors">
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="secondary">{c.industries[cs.industry as keyof typeof c.industries]}</Badge>
                        <Badge variant="outline">{c.services[cs.service as keyof typeof c.services]}</Badge>
                      </div>
                      <CardTitle className="text-xl">{cs.title}</CardTitle>
                      <p className="text-sm text-muted-foreground">{cs.client}</p>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold mb-1 text-muted-foreground">{uiLabels.challenge}</h4>
                        <p className="text-sm">{cs.challenge}</p>
                      </div>
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold mb-1 text-muted-foreground">{uiLabels.solution}</h4>
                        <p className="text-sm">{cs.solution}</p>
                      </div>
                      <div className="grid grid-cols-3 gap-2 pt-4 border-t border-border/50">
                        {cs.results.map((r, j) => (
                          <div key={j} className="text-center">
                            <div className="text-lg font-bold text-cyan">{r.metric}</div>
                            <div className="text-xs text-muted-foreground">{r.label}</div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))
            ) : (
              <Card className="md:col-span-2 lg:col-span-3 border-border/50">
                <CardContent className="py-10 text-center">
                  <p className="text-muted-foreground">{c.contactSubtitle}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan/10 to-orange/10">
        <div className="container mx-auto px-4 text-center">
          <TrendingUp className="h-12 w-12 text-cyan mx-auto mb-6" />
          <h2 className="text-3xl font-heading font-bold mb-4">{c.contactCTA}</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">{c.contactSubtitle}</p>
          <Link to={`/${language}/contact`}>
            <Button size="lg" className="bg-gradient-to-r from-orange to-orange-light text-white gap-2">
              {c.contactCTA}
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <RelatedPagesSection links={[
        { href: '/services/bpo', label: 'BPO & Call Center', description: '24/7 multilingual customer support outsourcing' },
        { href: '/services/digital-studio', label: 'Digital Studio', description: 'Custom web, mobile & SaaS development' },
        { href: '/pricing', label: 'Transparent Pricing', description: 'Per-hour, per-ticket & dedicated team models' },
        { href: '/roi-calculator', label: 'ROI Calculator', description: 'Calculate your outsourcing savings in 2 minutes' },
        { href: '/industries', label: 'Industries We Serve', description: 'E-commerce, SaaS, Fintech, Healthcare & more' },
        { href: '/contact', label: 'Start Your Success Story', description: 'Free assessment within 24 hours' },
      ]} />
    </MainLayout>
  );
}
